import React from 'react';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import { styled } from '@mui/system';

import { AppBarIcon } from 'components/AppBar';
import { Container } from 'components/Containers';
import PageLoadSpinner from 'components/PageLoadSpinner';
import { useAppBarData } from 'catering/hooks';
import { useGoToCart, useGoToOrderSettings } from 'catering/hooks/navigation';

import FulfillmentOptions from './FulfillmentOptions';
import IconTextButton from './IconTextButton';
import OpenCartButton from './OpenCartButton';
import muiPropForwarding from 'styles/helpers/muiPropForwarding';
import { getHeaderLinkLighterColor } from 'styles/helpers/getThemeStyles';

const StyledDivider = styled(Divider, {
  shouldForwardProp: muiPropForwarding('ownState')
})(({ theme }) => {
  return {
    display: 'none',
    height: 34,
    width: 1,
    backgroundColor: getHeaderLinkLighterColor(theme),
    margin: '0 8px',
    opacity: 0.5,
    [theme.breakpoints.up('md')]: {
      display: 'initial'
    }
  };
});

const StyledAppBar = styled(MuiAppBar)(({ theme }) => {
  return {
    '&.MuiAppBar-root': {
      backgroundColor: theme.palette.header.background.main
    }
  };
});

function MenuAppBar() {
  const goToCart = useGoToCart();
  const goToOrderSettings = useGoToOrderSettings();

  const { isLoading, data: appBarData } = useAppBarData();

  const handleCartButtonClick = () => goToCart();

  const handleFulfillmentOptionClick = fulfillmentMethod => {
    return goToOrderSettings({ locationSlug, fulfillmentMethod });
  };

  if (isLoading) {
    return <PageLoadSpinner />;
  }

  const {
    activeFulfillmentMethod,
    availableFulfillmentOptions,
    cart,
    fulfillmentAddress,
    fulfillmentDateTime,
    fulfillmentDateTimeRange,
    locationSlug
  } = appBarData;

  return (
    <StyledAppBar data-testid="menu-app-bar">
      <Container>
        <Toolbar
          sx={theme => ({
            '&.MuiToolbar-root': {
              height: theme.spacing(8.125),
              px: '5px',
              py: 0
            }
          })}
        >
          <Box
            sx={theme => ({
              display: 'flex',
              width: '100%',
              alignItems: 'center',
              justifyContent: 'space-between',
              [theme.breakpoints.up('sm')]: {
                width: 'unset'
              }
            })}
          >
            <FulfillmentOptions
              availableFulfillmentOptions={availableFulfillmentOptions}
              activeFulfilmentMethod={activeFulfillmentMethod}
              onFulfillmentOptionClick={handleFulfillmentOptionClick}
            />

            {fulfillmentDateTime && (
              <>
                <StyledDivider />
                <IconTextButton
                  label={fulfillmentDateTimeRange ?? fulfillmentDateTime}
                  onClick={() =>
                    handleFulfillmentOptionClick(activeFulfillmentMethod)
                  }
                  startIcon={<AppBarIcon icon="time" />}
                />
              </>
            )}

            {fulfillmentAddress && (
              <>
                <StyledDivider />
                <IconTextButton
                  sx={theme => ({
                    '&.MuiButton-text': {
                      display: 'none',
                      [theme.breakpoints.up('md')]: {
                        display: 'flex'
                      }
                    }
                  })}
                  label={fulfillmentAddress}
                  onClick={() =>
                    handleFulfillmentOptionClick(activeFulfillmentMethod)
                  }
                  startIcon={<AppBarIcon icon="address" />}
                />
              </>
            )}
          </Box>

          <Box
            sx={theme => ({
              [theme.breakpoints.up('sm')]: {
                width: 'unset'
              }
            })}
          >
            {!cart.isEmpty && (
              <OpenCartButton cart={cart} onClick={handleCartButtonClick} />
            )}
          </Box>
        </Toolbar>
      </Container>
    </StyledAppBar>
  );
}

export default MenuAppBar;
